import { useCallback, useEffect, useRef, useState } from "react";

function loadStoredPosition(storageKey, fallback) {
    try {
        const raw = localStorage.getItem(storageKey);
        if (raw) {
            const parsed = JSON.parse(raw);
            if (typeof parsed.x === "number" && typeof parsed.y === "number") return parsed;
        }
    } catch {
        /* ignore */
    }
    return fallback;
}

function defaultPosition(widgetId) {
    const w = typeof window !== "undefined" ? window.innerWidth : 1200;
    const h = typeof window !== "undefined" ? window.innerHeight : 800;
    if (widgetId === "team-chat") {
        return { x: Math.max(16, w - 200), y: Math.max(16, h - 150) };
    }
    return { x: Math.max(16, w - 200), y: Math.max(16, h - 80) };
}

export function useFloatingWidget(widgetId) {
    const storageKey = `crm_widget_pos_${widgetId}`;
    const [pos, setPos] = useState(() => loadStoredPosition(storageKey, defaultPosition(widgetId)));
    const [minimized, setMinimized] = useState(false);
    const draggingRef = useRef(false);
    const offsetRef = useRef({ x: 0, y: 0 });

    useEffect(() => {
        try {
            localStorage.setItem(storageKey, JSON.stringify(pos));
        } catch {
            /* ignore */
        }
    }, [pos, storageKey]);

    useEffect(() => {
        const onResize = () => {
            setPos((prev) => ({
                x: Math.min(prev.x, Math.max(16, window.innerWidth - 80)),
                y: Math.min(prev.y, Math.max(16, window.innerHeight - 48)),
            }));
        };
        window.addEventListener("resize", onResize);
        return () => window.removeEventListener("resize", onResize);
    }, []);

    const startDrag = useCallback((e) => {
        if (e.button !== 0) return;
        draggingRef.current = true;
        const rect = e.currentTarget.closest(".floating-widget-root")?.getBoundingClientRect();
        if (!rect) return;
        offsetRef.current = { x: e.clientX - rect.left, y: e.clientY - rect.top };
        e.currentTarget.setPointerCapture?.(e.pointerId);
        e.preventDefault();
    }, []);

    useEffect(() => {
        const onMove = (e) => {
            if (!draggingRef.current) return;
            const x = Math.max(8, Math.min(window.innerWidth - 72, e.clientX - offsetRef.current.x));
            const y = Math.max(8, Math.min(window.innerHeight - 48, e.clientY - offsetRef.current.y));
            setPos({ x, y });
        };
        const onUp = () => {
            draggingRef.current = false;
        };
        window.addEventListener("pointermove", onMove);
        window.addEventListener("pointerup", onUp);
        return () => {
            window.removeEventListener("pointermove", onMove);
            window.removeEventListener("pointerup", onUp);
        };
    }, []);

    const rootStyle = {
        position: "fixed",
        left: pos.x,
        top: pos.y,
        right: "auto",
        bottom: "auto",
        zIndex: 1040,
    };

    const dragHandleProps = {
        onPointerDown: startDrag,
        title: "Drag to move",
    };

    return {
        pos,
        minimized,
        setMinimized,
        minimize: () => setMinimized(true),
        restore: () => setMinimized(false),
        rootStyle,
        dragHandleProps,
    };
}
